import { LICENSE_APP_GROUP, licenseManagementDefaultConfig } from '@/constants';
import { licenseTypes, propertyDetailsTabs } from '@/data/data-selectors';
import { defaultWaitlistInfoConfig } from '@/license/license-constants';
import { useLicenseStatusInfo } from '@/license/utils/license-utils';
import { TStatsPropertyDetails } from '@/types';
import dayjs from 'dayjs';
import first from 'lodash/first';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ExpandedLicenseInfo } from '../expanded-license-info';
import { licenseHeaderLabels } from '../license-header/constants';
import { LicenseHeader } from '../license-header/license-header';
import {
  TLicenseHeaderConfig,
  TLicenseHeaderConfigs,
  TLicenseHeaderCustomColumn,
  TLicenseHeaderCustomHConfig,
} from '../license-header/types';
import { ExpandableLicenseButton } from './expandable-license-button';
import { TExpandableLicenseProps } from './types';

export function ExpandableLicense(props: TExpandableLicenseProps) {
  // "license" in this context is the license data
  const { index, license, isExpanded, handleExpand, refresh } = props;

  const { status, certificateInfo, misc, waitlistPosition } = license;

  const {
    // "license" in this context is a nested key of the jurisdiction configuration
    license: { config, configsByLicenseType },
  }: TStatsPropertyDetails['tabs'] = useSelector(propertyDetailsTabs);

  const licenseStatusInfo = useLicenseStatusInfo();

  const licenseTypesData = useSelector(licenseTypes);

  const strLicenseLabel = licenseTypesData?.[license?.type]?.name;

  /**
   * Check whether a license is still an application by the status of the license itself.
   * Custom statuses per jurisdiction can be configured via customer-config.
   * See "src/license/utils/license-utils.js"
   */
  const isLicenseApplication =
    first(licenseStatusInfo?.[status]?.group || []) === LICENSE_APP_GROUP;

  /**
   * This may contain additional configuration for the LicenseHeader component.
   * See "scripts/stats/ga-chatham-city_of_savannah.json" for an example.
   */
  const statsConfig: TLicenseHeaderConfig = configsByLicenseType
    ? configsByLicenseType[license?.type]
    : config;

  const licenseInfoConfig = useMemo(() => {
    if (license?.misc?.waitlist) {
      return statsConfig?.waitlistInfoConfig || defaultWaitlistInfoConfig;
    }

    return (
      statsConfig?.licenseInfoRenderConfig ||
      licenseManagementDefaultConfig.licenseInfoRenderConfig
    );
  }, [license?.misc?.waitlist, statsConfig]);

  /**
   * Handling processing of data for the license header summary
   */
  const pullColumnConfig = useCallback(
    (column: keyof TLicenseHeaderCustomHConfig) => {
      const permitTypeConfigPrefix: keyof TLicenseHeaderConfigs =
        isLicenseApplication
          ? 'applicationHeaderConfig'
          : 'licenseHeaderConfig';
      const path = `${permitTypeConfigPrefix}.${column}`;

      return get(statsConfig, path);
    },
    [isLicenseApplication, statsConfig]
  );

  const convertConfigToRenderData = useCallback(
    ([label, key]: [string, string]): [string, string] => [
      label,
      get(license, key, ''),
    ],
    [license]
  );

  const leftColumnConfig = useMemo(() => {
    const customLeftColumnConfig = pullColumnConfig('customLeftColumn');
    if (!isEmpty(customLeftColumnConfig))
      return customLeftColumnConfig.map(convertConfigToRenderData);

    const defaultApplicationSet: TLicenseHeaderCustomColumn = [
      [licenseHeaderLabels.appNumber, certificateInfo.applicationNumber],
      misc.waitlist
        ? [licenseHeaderLabels.waitlistPosition, waitlistPosition]
        : [licenseHeaderLabels.appDate, certificateInfo.applicationDate],
    ];

    const defaultLicenseSet: TLicenseHeaderCustomColumn = [
      [licenseHeaderLabels.licenseNumber, certificateInfo.licenseNumber],
      [licenseHeaderLabels.issueDate, certificateInfo.issueDate],
    ];

    return isLicenseApplication ? defaultApplicationSet : defaultLicenseSet;
  }, [
    certificateInfo,
    convertConfigToRenderData,
    isLicenseApplication,
    misc,
    pullColumnConfig,
    waitlistPosition,
  ]);

  const rightColumnConfig = useMemo(() => {
    const customRightColumnConfig = pullColumnConfig('customRightColumn');
    if (!isEmpty(customRightColumnConfig))
      return customRightColumnConfig.map(convertConfigToRenderData);

    const defaultApplicationSet: TLicenseHeaderCustomColumn = [
      misc.waitlist
        ? [
            licenseHeaderLabels.appDate,
            dayjs(misc.waitlistApplicationDate).format('YYYY-MM-DD h:mma'),
          ]
        : [licenseHeaderLabels.appName, certificateInfo.certificateHolder],
    ];

    const defaultLicenseSet: TLicenseHeaderCustomColumn = [
      [licenseHeaderLabels.licenseHolder, certificateInfo.certificateHolder],
      [licenseHeaderLabels.expirationDate, certificateInfo.expirationDate],
    ];

    return isLicenseApplication ? defaultApplicationSet : defaultLicenseSet;
  }, [
    certificateInfo,
    convertConfigToRenderData,
    isLicenseApplication,
    misc,
    pullColumnConfig,
  ]);

  /** Either the license or application number */
  const applicationLicenseNumber = isLicenseApplication
    ? license.certificateInfo.applicationNumber
    : license.certificateInfo.licenseNumber;
  const ariaLabel = `${isLicenseApplication ? 'Application' : 'License'}: ${applicationLicenseNumber}`;

  return (
    <div aria-label={ariaLabel}>
      <LicenseHeader
        isLicenseApplication={isLicenseApplication}
        license={license}
        licenseTitleText={strLicenseLabel}
        leftColumnConfig={leftColumnConfig}
        rightColumnConfig={rightColumnConfig}
      />

      <ExpandableLicenseButton
        handleExpand={handleExpand}
        index={index}
        isExpanded={Boolean(isExpanded)}
      />

      {isExpanded && (
        <ExpandedLicenseInfo
          index={index}
          license={license}
          licenseConfig={licenseInfoConfig}
          refresh={refresh}
          supplementalQuestionsConfig={statsConfig?.secondaryDisplay}
        />
      )}
    </div>
  );
}
